import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography, useTheme } from '@mui/material';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { type TFunction } from 'i18next';

import { LocationUserRolesAssignment } from '@/api/generated';
import DeactivateIcon from '@/assets/icons/deactivate.svg?react';
import EllipsisHorizontalIcon from '@/assets/icons/ellipsis-horizontal.svg?react';
import PencilIcon from '@/assets/icons/pencil.svg?react';
import BaseCell from '@/components/DataGrid/BaseCell';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import RoleDisplay from '@/containers/Users/RoleDisplay';
import { SETTINGS_STEP_KEYS, toUserSettings } from '@/root/linker';
import { getUserFullName } from '@/utils/user';

const columnHelper = createColumnHelper<LocationUserRolesAssignment>();

export const folderAccessColumns = (t: TFunction, onRemove?: (userAccess: LocationUserRolesAssignment) => void) =>
  [
    columnHelper.accessor('user', {
      header: () => t('orgSettings.folderAccess.columns.user'),
      cell: ({ getValue }) => {
        const user = getValue();

        return (
          <BaseCell>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="body2">{getUserFullName(user) || user?.name || user?.email}</Typography>
            </Stack>
          </BaseCell>
        );
      },
      enableSorting: false
    }),
    columnHelper.display({
      id: 'access',
      header: () => t('orgSettings.folderAccess.columns.access'),
      cell: ({ row }) => {
        const userId = row.original?.user?.id;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const navigate = useNavigate();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { spacing } = useTheme();

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleEditClick = useCallback(() => {
          navigate(
            toUserSettings({
              userId: userId!,
              stepKey: SETTINGS_STEP_KEYS.BASIC_INFO
            })
          );
        }, [navigate, userId]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleRemoveClick = useCallback(() => {
          onRemove?.(row.original);
        }, [row.original]);

        return (
          <BaseCell sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <RoleDisplay value={row.original.roles} />
            <DropdownMenu
              customIcon={<EllipsisHorizontalIcon />}
              menuProps={{
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'right'
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'right'
                }
              }}
              autoClose
            >
              <DropdownMenuItem
                text={t('orgSettings.folderAccess.actions.edit')}
                onClick={handleEditClick}
                icon={
                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    sx={{ '& .PencilIcon-root': { width: spacing(3), height: spacing(3) } }}
                  >
                    <PencilIcon className="PencilIcon-root" />
                  </Stack>
                }
              />
              <DropdownMenuItem
                text={t('orgSettings.folderAccess.actions.remove')}
                onClick={handleRemoveClick}
                icon={
                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    sx={{ '& .DeactivateIcon-root': { width: 20, height: 20, m: 0.25 } }}
                  >
                    <DeactivateIcon className="DeactivateIcon-root" />
                  </Stack>
                }
              />
            </DropdownMenu>
          </BaseCell>
        );
      },
      enableSorting: false
    })
  ] as ColumnDef<LocationUserRolesAssignment>[];
